import { z } from 'zod';

const FilterValueSchema = z.object({
  name: z.string(),
  id: z.string(),
  count: z.number(),
  selected: z.boolean(),
  enabled: z.boolean(),
  hex: z.string().nullable(),
  eventAction: z.string().nullable(),
});

const FilterTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  values: z.array(FilterValueSchema),
});

const FilterSchema = z.object({
  collapsed: z.boolean(),
  selected: z.boolean(),
  values: z.array(FilterValueSchema).nullable(),
  id: z.string(),
  name: z.string(),
  type: z.string(),
  types: z.array(FilterTypeSchema).nullable(),
  parameter: z.string(),
  eventAction: z.string(),
  enabled: z.boolean(),
  actionTokens: z.array(z.object({})),
  count: z.number(),
});

const ProductListPageSchema = z.object({
  category: z.object({ name: z.string(), key: z.string() }),
  productCount: z.number(),
  filters2: z.array(FilterSchema),
});

export const FiltersForCategorySchema = z.object({
  productListPage: ProductListPageSchema,
});

export const IndexableFiltersCategoriesSchema = z.object({
  catalogue: z.object({
    catalogueId: z.number(),
    catalogueName: z.string(),
    catalogueType: z.string(),
    globalCatalogueIdentifier: z.string(),
    catalogueIdentifierTranslations: z.string(),
    topCategories: z.array(z.string()),
  }),
  categories: z.array(
    z.object({
      externalCategoryId: z.string(),
      categoryId: z.number().transform((val) => `${val}`),
      globalCategoryName: z.string(),
      categoryNameTranslation: z.string().nullable(),
      productImage: z.string().nullable(),
      childCategories: z.array(z.string()),
    }),
  ),
});

export const AllIndexableFiltersSchema = z.array(
  z.object({
    internalCategoryId: z.number(),
    externalCategoryId: z.string(),
    indexableFilterDTOS: z.array(
      z.object({
        createdUser: z.string(),
        ruLocale: z.string().nullable(),
        categoryIndexableFilterId: z.number().transform((val) => `${val}`),
        title: z.string(),
        parentFilterId: z.number().nullable(),
        filterDetails: z.array(
          z.object({
            name: z.string().nullable(),
            filterType: z.string(),
            filterGlobalIds: z.array(z.string()),
          }),
        ),
      }),
    ),
  }),
);

export const FilterStatisticsSchema = z.object({
  totalIndexableFilters: z.number(),
  globalIndexableFilters: z.number(),
  localIndexableFilters: z.number(),
  relatedIndexableFilters: z.number(),
});

const CoverageProductCountSchema = z
  .array(
    z.object({
      filterId: z.string(),
      productCount: z.number(),
      filterName: z.string(),
    }),
  )
  .nullable();

export const FilterCoverageSchema = z.object({
  cannibalisedCategoryIds: z.array(z.string()).nullable(),
  existList: z.array(
    z.object({
      ruLocale: z.string(),
      productCountInfo: CoverageProductCountSchema,
    }),
  ),
  nonExistList: z.array(
    z.object({
      ruLocale: z.string(),
      productCountInfo: CoverageProductCountSchema,
    }),
  ),
  filterMarketsCoveragePercentage: z.string(),
});

export const FilterNotifications = z.array(
  z.object({
    notificationType: z.string(),
    actionDate: z.string(),
    requiredSeoAction: z.string(),
    affectedCategories: z.array(
      z.object({
        externalCategoryId: z.string(),
        redirectedCategoryId: z.string().nullable(),
        categoryName: z.string().nullable(),
        newCategoryName: z.string().nullable(),
        affectedIndexableFiltersTotalCount: z.number(),
        affectedGlobalIndexableFiltersCount: z.number(),
        affectedMarketIndexableFiltersCount: z.number(),
      }),
    ),
  }),
);

export const SuggestedActivatedSchema = z.array(
  z.object({
    externalCategoryId: z.string(),
    indexableFilterSuggestionStatus: z.enum([
      'suggestions_partially_created',
      'suggestions_fully_created',
      'suggestions_not_created',
      'suggestions_generated',
    ]),
  }),
);

export const SuggestedFilterForCategorySchema = z.array(
  z.object({
    categoryId: z.number(),
    filterType: z.string(),
    filterName: z.string(),
    filterId: z.string(),
    title: z.string(),
    marketCoverage: z.number(),
    keywordSearchVolume: z.number(),
    productCount: z.number(),
  }),
);
